import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ArrowRight, Sparkles, Target, TrendingUp, DollarSign, Users, 
  Calendar, CheckCircle, Clock, BookOpen, Briefcase, ChevronRight,
  AlertTriangle, Star, Lightbulb, Map, Rocket, Award, Building,
  GraduationCap, Network, PiggyBank, ArrowLeftRight, Play, Pause,
  Download, Trash2, Eye, RefreshCw, History
} from "lucide-react";

const CareerPivotPlanner = () => {
  const { token } = useContext(AuthContext);
  
  // Wizard state
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    current_career: "",
    current_role: "",
    years_experience: 3,
    target_career: "",
    target_role: "",
    timeline_months: 12,
    motivation: "",
    constraints: {
      financial_runway_months: 6,
      weekly_learning_hours: 10,
      can_take_pay_cut: false,
      prefer_part_time_transition: false,
      has_dependents: false,
      location_flexible: true
    }
  });
  
  // Generated plan
  const [currentPlan, setCurrentPlan] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // History
  const [savedPlans, setSavedPlans] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Success stories
  const [successStories, setSuccessStories] = useState([]);
  const [popularTransitions, setPopularTransitions] = useState([]);
  
  useEffect(() => {
    if (token) {
      fetchSavedPlans();
      fetchPopularTransitions();
    }
  }, [token]);
  
  useEffect(() => {
    if (formData.current_career && formData.target_career) {
      fetchSuccessStories();
    }
  }, [formData.current_career, formData.target_career]);
  
  const fetchSavedPlans = async () => {
    try {
      const response = await axios.get(`${API}/career-pivot/my-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedPlans(response.data.plans || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };
  
  const fetchSuccessStories = async () => {
    try {
      const response = await axios.get(`${API}/career-pivot/success-stories`, {
        params: { from_career: formData.current_career, to_career: formData.target_career }
      });
      setSuccessStories(response.data.stories || []);
    } catch (error) {
      console.error("Error fetching stories:", error);
    }
  };
  
  const fetchPopularTransitions = async () => {
    try {
      const response = await axios.get(`${API}/career-pivot/popular-transitions`);
      setPopularTransitions(response.data.transitions || []);
    } catch (error) {
      console.error("Error fetching transitions:", error);
    }
  };
  
  const generatePlan = async () => {
    if (!formData.current_career || !formData.target_career) {
      toast.error("Please fill in your current and target careers");
      return;
    }
    
    setIsGenerating(true);
    try {
      const response = await axios.post(
        `${API}/career-pivot/generate-plan`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCurrentPlan(response.data.plan);
      setStep(5); // Show results
      toast.success("Your personalized pivot plan is ready!");
      fetchSavedPlans(); // Refresh saved plans
    } catch (error) {
      console.error("Error generating plan:", error);
      toast.error("Failed to generate plan. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };
  
  const loadPlan = async (planId) => {
    try {
      const response = await axios.get(`${API}/career-pivot/plan/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentPlan(response.data.plan);
      setStep(5);
      setShowHistory(false);
    } catch (error) {
      toast.error("Failed to load plan");
    }
  };
  
  const deletePlan = async (planId) => {
    try {
      await axios.delete(`${API}/career-pivot/plan/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Plan deleted");
      fetchSavedPlans();
    } catch (error) {
      toast.error("Failed to delete plan");
    }
  };
  
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const updateConstraints = (field, value) => {
    setFormData(prev => ({
      ...prev,
      constraints: { ...prev.constraints, [field]: value }
    }));
  };
  
  const getRiskColor = (level) => {
    switch (level) {
      case "low": return "text-green-600 bg-green-100";
      case "medium": return "text-yellow-600 bg-yellow-100";
      case "high": return "text-red-600 bg-red-100";
      default: return "text-gray-600 bg-gray-100";
    }
  };
  
  const getFeasibilityColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  // Wizard Step 1: Current Career
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Briefcase className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Where are you now?</h2>
        <p className="text-gray-600 mt-2">Tell us about your current career situation</p>
      </div>
      
      <div className="grid gap-4">
        <div>
          <Label htmlFor="current_career">Current Career Field *</Label>
          <Input
            id="current_career"
            placeholder="e.g., Software Engineering, Marketing, Finance"
            value={formData.current_career}
            onChange={(e) => updateFormData("current_career", e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="current_role">Current Role/Title *</Label>
          <Input
            id="current_role"
            placeholder="e.g., Senior Developer, Marketing Manager"
            value={formData.current_role}
            onChange={(e) => updateFormData("current_role", e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Years of Experience: {formData.years_experience}</Label>
          <Slider
            value={[formData.years_experience]}
            onValueChange={(v) => updateFormData("years_experience", v[0])}
            min={0}
            max={30}
            step={1}
            className="mt-3"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Fresh Graduate</span>
            <span>30+ years</span>
          </div>
        </div>
      </div>
      
      {/* Popular transitions hint */}
      {popularTransitions.length > 0 && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm font-medium text-blue-800 mb-2">🔥 Popular Career Transitions</p>
          <div className="flex flex-wrap gap-2">
            {popularTransitions.slice(0, 5).map((t, i) => (
              <Badge 
                key={i} 
                variant="outline" 
                className="cursor-pointer hover:bg-blue-100"
                onClick={() => {
                  updateFormData("current_career", t.from);
                  updateFormData("target_career", t.to);
                }}
              >
                {t.from} → {t.to}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  
  // Wizard Step 2: Target Career
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Target className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Where do you want to go?</h2>
        <p className="text-gray-600 mt-2">Define your dream career destination</p>
      </div>
      
      <div className="grid gap-4">
        <div>
          <Label htmlFor="target_career">Target Career Field *</Label>
          <Input
            id="target_career"
            placeholder="e.g., Product Management, Data Science"
            value={formData.target_career}
            onChange={(e) => updateFormData("target_career", e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="target_role">Target Role/Title *</Label>
          <Input
            id="target_role"
            placeholder="e.g., Product Manager, Data Analyst"
            value={formData.target_role}
            onChange={(e) => updateFormData("target_role", e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Target Timeline: {formData.timeline_months} months</Label>
          <Slider
            value={[formData.timeline_months]}
            onValueChange={(v) => updateFormData("timeline_months", v[0])}
            min={3}
            max={36}
            step={1}
            className="mt-3"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>3 months (Aggressive)</span>
            <span>36 months (Relaxed)</span>
          </div>
        </div>
        
        <div>
          <Label htmlFor="motivation">Why do you want to make this change?</Label>
          <Textarea
            id="motivation"
            placeholder="e.g., I want more creative freedom, better work-life balance, higher earning potential..."
            value={formData.motivation}
            onChange={(e) => updateFormData("motivation", e.target.value)}
            className="mt-1"
            rows={3}
          />
        </div>
      </div>
      
      {/* Success stories preview */}
      {successStories.length > 0 && (
        <div className="mt-6 p-4 bg-green-50 rounded-lg">
          <p className="text-sm font-medium text-green-800 mb-2">✨ Others have made this transition!</p>
          <p className="text-sm text-green-700">
            We found {successStories.length} success stories from similar career pivots. You will see them in your plan.
          </p>
        </div>
      )}
    </div>
  );
  
  // Wizard Step 3: Constraints
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <PiggyBank className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Your Constraints</h2>
        <p className="text-gray-600 mt-2">Help us create a realistic plan for your situation</p>
      </div>
      
      <div className="grid gap-6">
        <div>
          <Label>Financial Runway: {formData.constraints.financial_runway_months} months of savings</Label>
          <Slider
            value={[formData.constraints.financial_runway_months]}
            onValueChange={(v) => updateConstraints("financial_runway_months", v[0])}
            min={0}
            max={24}
            step={1}
            className="mt-3"
          />
          <p className="text-xs text-gray-500 mt-1">How long can you sustain without income?</p>
        </div>
        
        <div>
          <Label>Weekly Learning Hours: {formData.constraints.weekly_learning_hours} hours</Label>
          <Slider
            value={[formData.constraints.weekly_learning_hours]}
            onValueChange={(v) => updateConstraints("weekly_learning_hours", v[0])}
            min={2}
            max={40}
            step={1}
            className="mt-3"
          />
          <p className="text-xs text-gray-500 mt-1">Time available for learning new skills</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Can Take Pay Cut</Label>
              <p className="text-xs text-gray-500">Initially lower salary okay?</p>
            </div>
            <Switch
              checked={formData.constraints.can_take_pay_cut}
              onCheckedChange={(v) => updateConstraints("can_take_pay_cut", v)}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Part-Time Transition</Label>
              <p className="text-xs text-gray-500">Prefer gradual change?</p>
            </div>
            <Switch
              checked={formData.constraints.prefer_part_time_transition}
              onCheckedChange={(v) => updateConstraints("prefer_part_time_transition", v)}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Has Dependents</Label>
              <p className="text-xs text-gray-500">Family to support?</p>
            </div>
            <Switch
              checked={formData.constraints.has_dependents}
              onCheckedChange={(v) => updateConstraints("has_dependents", v)}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Location Flexible</Label>
              <p className="text-xs text-gray-500">Can relocate if needed?</p>
            </div>
            <Switch
              checked={formData.constraints.location_flexible}
              onCheckedChange={(v) => updateConstraints("location_flexible", v)}
            />
          </div>
        </div>
      </div>
    </div>
  );
  
  // Wizard Step 4: Review & Generate
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Review Your Plan</h2>
        <p className="text-gray-600 mt-2">Confirm your details before we generate your personalized roadmap</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-blue-800">Current Position</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-semibold">{formData.current_role || "Not specified"}</p>
            <p className="text-sm text-gray-600">{formData.current_career || "Not specified"}</p>
            <p className="text-xs text-gray-500 mt-1">{formData.years_experience} years experience</p>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-green-800">Target Position</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-semibold">{formData.target_role || "Not specified"}</p>
            <p className="text-sm text-gray-600">{formData.target_career || "Not specified"}</p>
            <p className="text-xs text-gray-500 mt-1">{formData.timeline_months} month timeline</p>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Your Constraints</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gray-500" />
              <span>{formData.constraints.financial_runway_months}mo runway</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span>{formData.constraints.weekly_learning_hours}h/week learning</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className={`w-4 h-4 ${formData.constraints.can_take_pay_cut ? "text-green-500" : "text-gray-300"}`} />
              <span>Pay cut {formData.constraints.can_take_pay_cut ? "OK" : "No"}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className={`w-4 h-4 ${formData.constraints.prefer_part_time_transition ? "text-green-500" : "text-gray-300"}`} />
              <span>Part-time {formData.constraints.prefer_part_time_transition ? "Yes" : "No"}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className={`w-4 h-4 ${formData.constraints.has_dependents ? "text-orange-500" : "text-gray-300"}`} />
              <span>Dependents {formData.constraints.has_dependents ? "Yes" : "No"}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className={`w-4 h-4 ${formData.constraints.location_flexible ? "text-green-500" : "text-gray-300"}`} />
              <span>Relocate {formData.constraints.location_flexible ? "OK" : "No"}</span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {formData.motivation && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Your Motivation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 italic">&quot;{formData.motivation}&quot;</p>
          </CardContent>
        </Card>
      )}
      
      <Button 
        onClick={generatePlan} 
        disabled={isGenerating}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg"
      >
        {isGenerating ? (
          <>
            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
            AI is crafting your personalized plan...
          </>
        ) : (
          <>
            <Rocket className="w-5 h-5 mr-2" />
            Generate My Career Pivot Plan
          </>
        )}
      </Button>
    </div>
  );
  
  // Step 5: Results View
  const renderResults = () => {
    if (!currentPlan) return null;
    
    return (
      <div className="space-y-6">
        {/* Header with scores */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold">Your Career Pivot Plan</h2>
              <p className="text-purple-100 mt-1">
                {currentPlan.current_career} → {currentPlan.target_career}
              </p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{currentPlan.feasibility_score}</div>
              <div className="text-sm text-purple-100">Feasibility Score</div>
            </div>
          </div>
          
          <div className="flex items-center gap-4 mt-4">
            <Badge className={getRiskColor(currentPlan.risk_level)}>
              {currentPlan.risk_level?.toUpperCase()} RISK
            </Badge>
            <span className="text-sm text-purple-100">
              Timeline: {currentPlan.timeline_months} months
            </span>
          </div>
        </div>
        
        {/* Executive Summary */}
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-700 leading-relaxed">{currentPlan.executive_summary}</p>
          </CardContent>
        </Card>
        
        {/* Tabs for different sections */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="skills" className="text-xs">Skills</TabsTrigger>
            <TabsTrigger value="tasks" className="text-xs">Tasks</TabsTrigger>
            <TabsTrigger value="network" className="text-xs">Network</TabsTrigger>
            <TabsTrigger value="finance" className="text-xs">Finance</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* Milestones */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Key Milestones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {currentPlan.milestones?.map((milestone, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-bold shrink-0">
                        M{milestone.month}
                      </div>
                      <div>
                        <h4 className="font-semibold">{milestone.title}</h4>
                        <p className="text-sm text-gray-600">{milestone.description}</p>
                        <p className="text-xs text-gray-400 mt-1">🎉 {milestone.celebration_suggestion}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* First Role Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-500" />
                  Target Roles to Apply For
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {currentPlan.first_role_recommendations?.map((role, idx) => (
                    <Badge key={idx} variant="outline" className="py-2 px-3">
                      {role}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Bridge Roles */}
            {currentPlan.bridge_roles?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowLeftRight className="w-5 h-5 text-purple-500" />
                    Bridge Roles (Stepping Stones)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {currentPlan.bridge_roles.map((role, idx) => (
                      <div key={idx} className="p-4 bg-purple-50 rounded-lg">
                        <h4 className="font-semibold text-purple-900">{role.role_title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{role.why_good_bridge}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-xs text-gray-500">{role.typical_duration}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {role.skills_gained?.map((skill, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="skills" className="space-y-4 mt-4">
            {/* Transferable Skills */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Your Transferable Skills
                </CardTitle>
                <CardDescription>Skills you already have that transfer to your new career</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {currentPlan.transferable_skills?.map((skill, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div>
                        <h4 className="font-medium">{skill.skill_name}</h4>
                        <p className="text-xs text-gray-600">{skill.how_it_transfers}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={
                          skill.relevance_to_target === "high" ? "bg-green-500" :
                          skill.relevance_to_target === "medium" ? "bg-yellow-500" : "bg-gray-400"
                        }>
                          {skill.relevance_to_target} relevance
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          {skill.current_proficiency} level
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Skill Gaps */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Skill Gaps to Bridge
                </CardTitle>
                <CardDescription>Skills you need to develop for this transition</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {currentPlan.skill_gaps?.map((gap, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{gap.skill_name}</h4>
                        <Badge variant={
                          gap.importance === "critical" ? "destructive" :
                          gap.importance === "important" ? "default" : "secondary"
                        }>
                          {gap.importance}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <span>{gap.current_level}</span>
                        <ArrowRight className="w-4 h-4" />
                        <span className="font-medium">{gap.required_level}</span>
                        <span className="text-gray-400">|</span>
                        <Clock className="w-4 h-4" />
                        <span>{gap.estimated_time_weeks} weeks</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {gap.learning_resources?.map((resource, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            📚 {resource}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="tasks" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Weekly Action Plan
                </CardTitle>
                <CardDescription>Your week-by-week roadmap to success</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {currentPlan.weekly_tasks?.map((task, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${
                        task.milestone ? "border-yellow-300 bg-yellow-50" : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-600">
                            W{task.week_number}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{task.task}</p>
                            {task.milestone && (
                              <p className="text-xs text-yellow-700 mt-1">🎯 Milestone: {task.milestone}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-xs">
                            {task.category}
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">{task.estimated_hours}h</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="network" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Network className="w-5 h-5 text-purple-500" />
                  Networking Strategy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {currentPlan.networking_strategy?.map((target, idx) => (
                    <div key={idx} className="p-4 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-purple-500">{target.target_type?.replace(/_/g, " ")}</Badge>
                        <span className="text-sm text-gray-500">{target.timeline}</span>
                      </div>
                      <p className="text-sm text-gray-700">{target.description}</p>
                      <ul className="mt-2 space-y-1">
                        {target.action_items?.map((item, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                            <ChevronRight className="w-4 h-4 text-purple-400" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Success Stories */}
            {successStories.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    Success Stories
                  </CardTitle>
                  <CardDescription>People who made similar transitions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {successStories.map((story, idx) => (
                      <div key={idx} className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{story.from_career} → {story.to_career}</Badge>
                          <span className="text-sm text-gray-500">{story.timeline_months} months</span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{story.outcome}</p>
                        <p className="text-sm text-blue-600 italic">💡 &quot;{story.advice}&quot;</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="finance" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Financial Planning
                </CardTitle>
              </CardHeader>
              <CardContent>
                {currentPlan.financial_plan && (
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-gray-600">Current Salary Estimate</p>
                        <p className="text-xl font-bold text-blue-600">
                          {currentPlan.financial_plan.current_salary_estimate}
                        </p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-gray-600">Target Salary Range</p>
                        <p className="text-xl font-bold text-green-600">
                          {currentPlan.financial_plan.target_salary_range}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <p className="text-sm text-gray-600">Recommended Savings</p>
                        <p className="font-semibold text-orange-600">
                          {currentPlan.financial_plan.recommended_savings}
                        </p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-gray-600">Monthly Learning Budget</p>
                        <p className="font-semibold text-purple-600">
                          {currentPlan.financial_plan.monthly_learning_budget}
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <p className="font-medium mb-2">Potential Income During Transition</p>
                      <div className="flex flex-wrap gap-2">
                        {currentPlan.financial_plan.potential_income_during_transition?.map((item, i) => (
                          <Badge key={i} variant="outline">{item}</Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <p className="font-medium mb-2">Financial Milestones</p>
                      <ul className="space-y-2">
                        {currentPlan.financial_plan.financial_milestones?.map((item, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setCurrentPlan(null); setStep(1); }} className="flex-1">
            <RefreshCw className="w-4 h-4 mr-2" />
            Create New Plan
          </Button>
          <Button variant="outline" onClick={() => setShowHistory(true)}>
            <History className="w-4 h-4 mr-2" />
            View History
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50" data-testid="career-pivot-planner">
      <Navbar />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
            <Map className="w-4 h-4" />
            Career Pivot Planner
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Plan Your Career Transition
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Get a personalized, week-by-week roadmap to successfully transition to your dream career
          </p>
        </div>
        
        {/* History Button */}
        {savedPlans.length > 0 && step < 5 && (
          <div className="flex justify-end mb-4">
            <Button variant="outline" size="sm" onClick={() => setShowHistory(true)}>
              <History className="w-4 h-4 mr-2" />
              View Saved Plans ({savedPlans.length})
            </Button>
          </div>
        )}
        
        {/* Progress indicator (for wizard) */}
        {step < 5 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all ${
                    s === step 
                      ? "bg-purple-600 text-white scale-110" 
                      : s < step 
                      ? "bg-green-500 text-white" 
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {s < step ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 4 && (
                  <div className={`w-12 h-1 rounded ${s < step ? "bg-green-500" : "bg-gray-200"}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
        
        {/* Main Content */}
        <Card className="shadow-xl border-0">
          <CardContent className="p-8">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
            {step === 5 && renderResults()}
            
            {/* Navigation buttons (for wizard) */}
            {step < 5 && step !== 4 && (
              <div className="flex justify-between mt-8">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(Math.max(1, step - 1))}
                  disabled={step === 1}
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(Math.min(4, step + 1))}
                  className="bg-gradient-to-r from-purple-600 to-pink-600"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
            
            {step === 4 && (
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={() => setStep(3)}>
                  Back
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      
      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Your Saved Plans</DialogTitle>
            <DialogDescription>View and manage your career pivot plans</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {savedPlans.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No saved plans yet</p>
            ) : (
              savedPlans.map((plan) => (
                <div key={plan.id} className="p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">
                        {plan.current_career} → {plan.target_career}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {plan.current_role} → {plan.target_role}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Created: {new Date(plan.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getRiskColor(plan.risk_level)}>
                        {plan.feasibility_score}%
                      </Badge>
                      <Button size="sm" variant="ghost" onClick={() => loadPlan(plan.id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deletePlan(plan.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {plan.progress_percentage > 0 && (
                    <div className="mt-2">
                      <Progress value={plan.progress_percentage} className="h-2" />
                      <p className="text-xs text-gray-500 mt-1">{plan.progress_percentage}% complete</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default CareerPivotPlanner;
